import { createStore, createEvent, createEffect, combine } from 'effector';
import { PaginationProps } from 'antd/lib/pagination';
import { get } from 'lodash';
import moment from 'moment';

import request from '@helpers/request';
import api from '@constants/endpoints';
import createPaginationStore from './pagination';
import createPreloadersStore from './preloaders';
import createFiltersStore from './filters';
import removeUndefinedFilters from '@helpers/removeUndefinedFilters';
import IRegistrationIp from '@interfaces/IRegistrationIp';

export const reset = createEvent('Registration IPs store reset');

const defaultFilters = {
  sort: 'lastRegistrationDate,desc',
  statuses: undefined,
  onlyWithBannedAccounts: true,
  minBannedAccountsRatio: undefined,
  lastRegistrationDateFrom: moment()
    .subtract(14, 'days')
    .startOf('day')
    .format('YYYY-MM-DDTHH:mm:ss'),
  lastRegistrationDateTo: undefined,
  minTotalAccountsCount: 1,
  maxTotalAccountsCount: 100,
};

export const getList = createEffect({
  name: 'getList',
  async handler(params?: {
    pagination?: PaginationProps;
    filters?: Record<string, any>;
    sorter?: any;
  }) {
    const paginationParams: PaginationProps = {
      ...pagination.getState(),
      ...params?.pagination,
    };

    const filtersParams = params?.filters ?? filters.getState();

    const sorterParams: { sort?: string; sortBy?: string; sortDirection?: string } = {
      sort: filtersParams.sort,
    };

    if (params?.sorter) {
      const sortField = params.sorter.field || params.sorter.columnKey;

      if (params.sorter.order && sortField) {
        sorterParams.sort = `${sortField},${params.sorter.order.replace('end', '')}`;
      } else {
        sorterParams.sort = undefined;
      }
    }

    if (sorterParams.sort) {
      const [sortBy, sortDirection] = sorterParams.sort.split(',');
      sorterParams.sortBy = sortBy;
      sorterParams.sortDirection = sortDirection;
    } else {
      sorterParams.sortBy = undefined;
      sorterParams.sortDirection = undefined;
    }

    if (params?.sorter || params?.filters) {
      setFilters({
        ...filtersParams,
        sort: sorterParams.sort,
      });
    }

    if (params?.filters) {
      paginationParams.current = 1;
    }

    const { current, pageSize } = paginationParams;

    const mergedFilters = {
      ...defaultFilters,
      ...filtersParams,
    };

    const { sort, onlyWithBannedAccounts, statuses, ...restFilters } = mergedFilters;

    const normalizedFilters = removeUndefinedFilters({
      ...restFilters,
      statuses,
      minBannedAccountsCount: onlyWithBannedAccounts ? 1 : undefined,
    });

    const data = {
      ...normalizedFilters,
      pagination: {
        type: 'PAGE',
        pageSize: Number(pageSize ?? localStorage.getItem('registrationIpsPageSize') ?? 10),
        page: (current || 1) - 1,
      },
      ...(sorterParams.sortBy && sorterParams.sortDirection
        ? {
            sortBy: sorterParams.sortBy,
            sortDirection: sorterParams.sortDirection,
          }
        : {}),
    };

    const response = await request(api.registrationIps.getList, {
      data,
    });

    return response;
  },
});

export const banRegistrationIp = createEffect({
  name: 'banRegistrationIp',
  async handler({ ip }: { ip: string; preloaderId?: string }) {
    const response = await request(api.registrationIps.ban, {
      data: {
        inetAddress: ip,
      },
    });

    return response;
  },
});

export const unbanRegistrationIp = createEffect({
  name: 'unbanRegistrationIp',
  async handler({ ip }: { ip: string; preloaderId?: string }) {
    const response = await request(api.registrationIps.unban, {
      data: {
        inetAddress: ip,
      },
    });

    return response;
  },
});

const updateListHandler = () => {
  getList();
};

const { pagination, setPagination } = createPaginationStore({
  getList,
  listName: 'registrationIps',
  resetEvent: reset,
  bodyPath: ['data', 'body'],
});

export { setPagination };

const preloaders = createPreloadersStore([getList, banRegistrationIp, unbanRegistrationIp]);

const { filters, setFilters } = createFiltersStore({
  listName: 'registrationIps',
  deaultValues: defaultFilters,
  resetEvent: reset,
});

export { setFilters };

const list = createStore<IRegistrationIp[]>([])
  .on(getList.done, (_, { result }) => get(result, ['data', 'body', 'page'], []))
  .on(banRegistrationIp.done, updateListHandler)
  .on(unbanRegistrationIp.done, updateListHandler)
  .reset(reset);

const registrationIps = combine({
  preloaders,
  list,
  pagination,
  filters,
  setPagination,
});

export default registrationIps;
